// Drop this into each row of the Team Roster page:
//   <BusinessCardButton member={row} />
// Clicking it downloads one zip with the member's card front + back as PNGs.

import { useState } from 'react';
import { cardInfoFromRoster, missingCardFields, type RosterRowForCard } from './cardData';

export function BusinessCardButton({ member }: { member: RosterRowForCard }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const missing = missingCardFields(cardInfoFromRoster(member));
  const disabled = busy || missing.length > 0;

  async function onClick() {
    setBusy(true);
    setError(null);
    try {
      // Loaded on demand so the card fonts/images don't weigh down the roster page.
      const { downloadBusinessCards } = await import('./downloadBusinessCards');
      await downloadBusinessCards(member);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error && e.name === 'CardFitError' ? e.message : 'Could not make the card. Try again.');
    } finally {
      setBusy(false);
    }
  }

  // Styled like the Team Roster page's other row buttons (✎ Edit).
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={missing.length ? `Add ${missing.join(', ')} to the roster first` : 'Download business card (zip with the front + back PNGs)'}
        className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
      >
        {busy ? 'Making card…' : '↓ Business card'}
      </button>
      {missing.length > 0 && <small className="text-[10px] font-semibold text-amber-600">Needs {missing.join(', ')}</small>}
      {error && <small role="alert" className="text-[10px] font-semibold text-red-600">{error}</small>}
    </span>
  );
}
